export const EXTERNAL_BUSINESS_RELATIONSHIPS = [
  'owner',
  'partner',
  'director',
  'authorized_representative',
];

export const IDENTITY_RELATIONSHIPS = [
  'self',
  'authorized_contact',
  'guardian',
  'officer',
  'other',
];

export const BUSINESS_ROLE_FOR_RELATIONSHIP = {
  owner: 'proprietor',
  partner: 'partner',
  director: 'director',
};

export const scopeLabel = scope => ({
  view: 'View Business and tax account',
  pay: 'Pay Business tax',
  view_receipts: 'View receipts',
  download_receipts: 'Download receipts',
  manage_profile: 'Update permitted Business profile fields',
  manage_tax: 'Manage tax correspondence',
  submit_documents: 'Submit supporting documents',
  submit_tax_declaration: 'Submit annual tax declarations',
}[scope] || scope.replaceAll('_', ' '));

export const uniqueParties = parties => [...new Map(
  (parties || []).filter(Boolean).map(party => [party.party_uid, party])
).values()];

export const roleForSelectedParty = ({ business, partyUid, relationship, today = new Date().toISOString().slice(0, 10) }) => {
  const requiredRole = BUSINESS_ROLE_FOR_RELATIONSHIP[relationship];
  if (!requiredRole || !partyUid) return null;
  return (business?.active_party_roles || []).find(role => (
    role.party?.party_uid === partyUid
    && role.role_type === requiredRole
    && (!role.valid_from || String(role.valid_from).slice(0, 10) <= today)
    && (!role.valid_to || String(role.valid_to).slice(0, 10) >= today)
  )) || null;
};

export const buildCitizenApprovalPayload = ({ note, householdId, relationToHead }) => ({
  note: String(note || '').trim() || 'Approved by admin',
  household_id: householdId ? parseInt(householdId, 10) : null,
  relation_to_head: relationToHead || 'other',
});

export const buildExternalApprovalPayload = values => {
  const payload = {
    note: String(values.note || '').trim(),
    business_uid: values.businessUid,
    party_uid: values.partyUid || undefined,
    create_new_party: Boolean(values.createNewParty),
    identity_relationship: values.identityRelationship,
    business_relationship: values.businessRelationship,
    approved_scopes: values.approvedScopes?.length
      ? [...new Set(values.approvedScopes)]
      : ['view', 'pay', 'view_receipts', 'download_receipts', 'manage_profile', 'manage_tax', 'submit_documents', 'submit_tax_declaration'],
    create_business_role: Boolean(values.createBusinessRole),
  };
  if (payload.create_business_role) {
    payload.business_role_type = BUSINESS_ROLE_FOR_RELATIONSHIP[payload.business_relationship];
    payload.is_primary = Boolean(values.isPrimary);
    if (['proprietor', 'partner'].includes(payload.business_role_type)) {
      payload.ownership_share = Number(values.ownershipShare);
    }
  }
  return payload;
};

export const validateExternalApproval = values => {
  if (!values.businessUid) return 'Select the exact Business.';
  if (Boolean(values.partyUid) === Boolean(values.createNewParty)) {
    return 'Select one existing Party or confirm creation of a new Party.';
  }
  if (!IDENTITY_RELATIONSHIPS.includes(values.identityRelationship)) return 'Select the exact identity relationship.';
  if (values.createNewParty && values.identityRelationship !== 'self') return 'A new Party created from the applicant must use the self identity relationship.';
  if (values.partyType === 'organization' && values.identityRelationship === 'self') return 'Choose an organization relationship such as officer or authorized contact.';
  if (values.partyType && values.partyType !== 'organization' && values.identityRelationship === 'officer') return 'The officer relationship requires an organization Party.';
  if (!EXTERNAL_BUSINESS_RELATIONSHIPS.includes(values.businessRelationship)) return 'Select the exact Business relationship.';
  if (!String(values.note || '').trim() || String(values.note).trim().length < 5) return 'Enter an evidence review note of at least 5 characters.';
  const requiredRole = BUSINESS_ROLE_FOR_RELATIONSHIP[values.businessRelationship];
  if (requiredRole && !values.existingRole && !values.createBusinessRole) {
    return 'Reuse a current matching role or explicitly create the missing Business role.';
  }
  if (values.createBusinessRole && values.businessVerified) {
    return 'Use the approved registry role-change workflow before linking this account to a verified Business.';
  }
  if (values.createBusinessRole && ['proprietor', 'partner'].includes(requiredRole)) {
    const share = Number(values.ownershipShare);
    if (!Number.isFinite(share) || share <= 0 || share > 100) return 'Enter the exact ownership share (greater than 0 and at most 100).';
  }
  return '';
};
